/* External */
import {
  useEffect,
  useMemo,
  useCallback,
  memo,
} from 'react';
import PropTypes from 'prop-types';
import { NavLink, useLocation, matchPath } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

/* Ott-common */
import Icon from '@dtvgo/icon';

/* Utils */
import { getLink, getPath, RouteShape } from '../../utils/router';
import withInteractiveFocusable from '../../hocs/withInteractiveFocusable.hoc';
import useQuery from '../../hooks/useQuery';

function Item({
  className = '',
  focused,
  to,
  icon = '',
  label = '',
  sidebarFocused = false,
  setFocus,
  onMouseDown,
  onMouseEnter,
  pageId = '',
  isNavigatingIntoSideBar = false,
  children,
}) {
  const path = useMemo(() => (to ? getPath(to) : undefined), [to]);
  const link = useMemo(() => (to ? getLink(to) : ''), [to]);
  const { pathname } = useLocation() || {};
  const { source } = useQuery();

  useEffect(() => {
    if (path) {
      const match = matchPath(pathname, { path, exact: true });
      if (sidebarFocused && !isNavigatingIntoSideBar && (match || source === pageId)) {
        setFocus();
      }
    }
  }, [isNavigatingIntoSideBar, pageId, path, pathname, setFocus, sidebarFocused, source]);

  const handleClickLink = useCallback((event) => {
    event.preventDefault();
  }, []);

  return (
    <li
      className={`dtv-html5-c-sidebar__item ${focused ? 'dtv-html5-c-sidebar__item--focused' : ''} ${className}`}
      onMouseEnter={onMouseEnter}
    >
      <NavLink
        className={children ? 'dtv-html5-c-sidebar__linkavatar' : 'dtv-html5-c-sidebar__link'}
        activeClassName="dtv-html5-c-sidebar__link--active"
        onClick={handleClickLink}
        onMouseDown={onMouseDown}
        exact
        to={link}
      >

        {children ?? <Icon name={icon} className="dtv-html5-c-sidebar__icon" />}
        {children
          ? (
            <span className="dtv-html5-c-sidebar__text" id="text-profile">
              <FormattedMessage id={label} />
            </span>
          )
          : (
            <span className="dtv-html5-c-sidebar__text" style={{ textTransform: label === 'sidebar.item.myPage' ? 'none' : 'uppercase' }}>
              <FormattedMessage id={label} />
            </span>
          )}
        <hr className="dtv-html5-c-sidebar__line" />
      </NavLink>
    </li>
  );
}

Item.propTypes = {
  className: PropTypes.string,
  focused: PropTypes.bool.isRequired,
  label: PropTypes.string,
  to: RouteShape,
  icon: PropTypes.string,
  pageId: PropTypes.string,
  sidebarFocused: PropTypes.bool,
  setFocus: PropTypes.func.isRequired,
  onMouseDown: PropTypes.func,
  onMouseEnter: PropTypes.func,
  isNavigatingIntoSideBar: PropTypes.bool,
  children: PropTypes.node,
};

export default memo(withInteractiveFocusable()(memo(Item)));
